import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAdminWorkspaceTenantId } from "./admin-workspace-tenant";
import { isPublicPath } from "./public-routes";

/** Exported so pages using raw `fetch` can send the same Bearer token as `apiRequest`. */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    const employeeToken = localStorage.getItem("employeeToken");
    if (employeeToken) {
      console.log("[DEBUG] queryClient: Attaching token:", employeeToken.substring(0, 10) + "...");
      headers["Authorization"] = `Bearer ${employeeToken}`;
    } else {
      console.log("[DEBUG] queryClient: No employeeToken found in localStorage");
    }
    const workspaceTenant = getAdminWorkspaceTenantId();
    if (workspaceTenant) {
      headers["X-Tenant-Id"] = workspaceTenant;
    }
  } catch (e) {
    // Ignore localStorage access errors (e.g. SecurityError in private mode/iframe)
  }
  return headers;
}

/**
 * The zero-trust API gate answers an expired/absent admin session with exactly this
 * body. Without a handler the CRM keeps rendering its cached data and every button
 * surfaces a bare "Unauthorized API access" toast, so bounce to the login screen
 * instead. Portal/public 401s use different bodies and are deliberately left alone.
 */
const ADMIN_SESSION_EXPIRED_BODY = "Unauthorized API access";
const ADMIN_LOGIN_PATH = "/employee-portal/login";
export const SESSION_EXPIRED_FLAG = "adminSessionExpired";
let sessionExpiryHandled = false;

function handleAdminSessionExpired(res: Response, body: string): boolean {
  if (res.status !== 401 || !body.includes(ADMIN_SESSION_EXPIRED_BODY)) return false;

  // Never drag a customer off a public / token-gated page. They have no CRM
  // account, so "your session expired, sign in again" is nonsense to them — and
  // it silently replaced real quotes with a staff login screen. Admin-only
  // queries that leak onto these pages just fail quietly instead.
  if (isPublicPath(window.location.pathname)) return true;

  if (sessionExpiryHandled) return true;
  sessionExpiryHandled = true;

  try {
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("employeeData");
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  } catch {
    // Ignore storage access errors (private mode / iframe)
  }

  if (!window.location.pathname.startsWith(ADMIN_LOGIN_PATH)) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${ADMIN_LOGIN_PATH}?returnTo=${returnTo}`;
  }
  return true;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    if (handleAdminSessionExpired(res, text)) {
      throw new Error("Your session has expired. Please sign in again.");
    }
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Strip the `NNN: ` prefix throwIfResNotOk puts in front of the response body.
 * Deliberately character checks rather than a regex. The version this replaced
 * looked for a literal "d" three times over and a literal "s", having lost both of
 * its escapes to an editing path that collapses backslashes. It never matched, so
 * every toast read `400: {"message":"…"}` verbatim. Nothing here can be collapsed.
 */
function stripStatusPrefix(raw: string): string {
  const isDigit = (c: string) => c >= "0" && c <= "9";
  if (raw.length > 3 && raw[3] === ":" && isDigit(raw[0]) && isDigit(raw[1]) && isDigit(raw[2])) {
    return raw.slice(4).trimStart();
  }
  return raw;
}

/** The response body of an apiRequest error as text, minus the `NNN: ` prefix. */
function errorText(e: unknown): string {
  return stripStatusPrefix(e instanceof Error ? e.message : String(e ?? "")).trim();
}

/**
 * The JSON object an API error body carries, or null when the body was not JSON.
 *
 * For the call sites that need more than the message — a `details` suffix, a `code`,
 * an `errorType` that swaps in a bespoke sentence. Six of them used to reach these
 * fields by hand-rolling `indexOf(": ")` + slice + JSON.parse, which meant six more
 * copies of the prefix strip errorMessage() had already got wrong. Each composes its
 * own sentence out of the fields, so they need the object, not a finished string.
 */
export function errorBody(e: unknown): Record<string, any> | null {
  try {
    const parsed = JSON.parse(errorText(e));
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, any>;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

/**
 * Pull the human half out of an apiRequest error. throwIfResNotOk throws
 * `${status}: ${body}` where the body is usually {"message":"…"}, so a bare
 * e.message in a toast reads like "400: {\"message\"…" — or, worse, gets replaced
 * by a generic "Save failed" that tells the user nothing about what to change.
 */
export function errorMessage(e: unknown, fallback = "Something went wrong."): string {
  const message = errorBody(e)?.message;
  if (typeof message === "string" && message.trim()) return message;
  return errorText(e) || fallback;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { signal?: AbortSignal },
): Promise<Response> {
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
  };
  if (data) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    signal: options?.signal,
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Drop-in replacement for a bare fetch of an admin API path that carries the same
 * credentials as `apiRequest`. A plain fetch relies solely on the employeeSession
 * cookie, so the moment that cookie lapses the request hits the zero-trust gate
 * with nothing and 401s — silently, because hand-rolled queryFns swallow it into
 * an empty panel rather than the session-expired redirect. Use this for any GET
 * written as a raw fetch instead of going through the default queryFn.
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    credentials: "include",
    headers: { ...getAuthHeaders(), ...(init?.headers as Record<string, string> | undefined) },
  });
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      const res = await fetch(queryKey.join("/") as string, {
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchOnWindowFocus: true,
      staleTime: 5000, // Consider data stale after 5 seconds
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
